<!--
Copyright 2020 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <div v-if="loading === 'tab'">
    <loading :state="showsLoading"/>
    <component :is="component" v-if="component != null" v-bind="propsAndAttrs"/>
  </div>
  <page-body v-else-if="showsLoading">
    <loading :state="true"/>
  </page-body>
  <component :is="component" v-else-if="component != null" v-bind="propsAndAttrs"/>
</template>

<script>
import { markRaw } from 'vue';

import Loading from './loading.vue';
import PageBody from './page/body.vue';

import { loadAsync, loadedAsync } from '../util/load-async';
import { noop } from '../util/util';

export default {
  name: 'AsyncRoute',
  components: { Loading, PageBody },
  inject: ['alert'],
  inheritAttrs: false,
  // See routes.js for more information about these props.
  props: {
    componentName: {
      type: String,
      required: true
    },
    props: {
      type: Object,
      required: true
    },
    // `loading` determines how the loading message is rendered. I'm not sure
    // that the approach here of using a string prop is quite right. However,
    // since there are only two options at the moment, I think it's OK to use a
    // string prop for now. We may wish to revisit this if we add more options.
    loading: {
      type: String,
      required: true
    },
    // "k" for "key". (`key` is a reserved prop name.)
    k: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      component: null,
      showsLoading: false,
      cancel: noop
    };
  },
  computed: {
    propsAndAttrs() {
      // The main use of this.$attrs is to pass along event listeners to the
      // component.
      return { ...this.props, ...this.$attrs };
    },
    componentNameAndKey() {
      return [this.componentName, this.k];
    }
  },
  watch: {
    componentNameAndKey([newComponentName], [oldComponentName]) {
      if (newComponentName !== oldComponentName) {
        this.load();
      } else if (this.component != null) {
        /*
        If this.k has changed, then we need to re-render the component (unless
        this.component == null, in which case there is no component to
        re-render). We will cause a re-render by setting this.component to
        `null` for a tick.

        Previously, we used the `key` attribute to cause the component to
        re-render. However, that results in the following lifecycle stages:

          - `beforeUnmount` for the old component
          - `setup` for the new component
          - `unmounted` for the old component

        Because we use `unmounted` hooks with requestData, we need `unmounted`
        for the old component to happen before `setup` for the new component.
        Otherwise, the new component might try to create a local resource with
        the same name as one created by the old component, whose local resources
        haven't been removed yet.
        */
        const { component } = this;
        this.component = null;
        this.$nextTick(() => { this.component = component; });
      }
    }
  },
  created() {
    this.load();
  },
  beforeUnmount() {
    this.cancel();
  },
  methods: {
    load() {
      this.cancel();

      this.component = null;
      this.showsLoading = !loadedAsync(this.componentName);
      let canceled = false;
      this.cancel = () => {
        canceled = true;
      };
      loadAsync(this.componentName)()
        /*
        A few things can happen between when the async component starts loading
        and when it finishes:

          - this.componentName can change. This often happens after a route
            change (though not always, especially for a component associated
            with a parent route).
          - this.componentName can change, then change back. In that case, there
            are multiple pending promises for the same async component, and they
            will each now be resolved (though all but the last have been
            canceled).
          - The AsyncRoute component may be unmounted.
        */
        .then(m => {
          if (!canceled) {
            this.showsLoading = false;
            this.component = markRaw(m.default);
          }
        })
        .catch(() => {
          if (!canceled) {
            // It would be ideal to show a more informative error message.
            // However, the error seems to provide limited information. For
            // example, if there is a 404 because Frontend was rebuilt, the
            // resulting error is a ChunkLoadError with a `type` property equal
            // to 'error'.
            this.alert.danger(this.$t('alert.loadError'));
            this.showsLoading = false;
          }
        });
    }
  }
};
</script>

<i18n lang="json5">
{
  "en": {
    "alert": {
      "loadError": "The page you requested could not be loaded. Please refresh the page and try again."
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "alert": {
      "loadError": "Požadovanou stránku se nepodařilo načíst. Obnovte prosím stránku a zkuste to znovu."
    }
  },
  "de": {
    "alert": {
      "loadError": "Die Seite konnte nicht geladen werden. Bitte aktualisiere die Seite und versuche es erneut."
    }
  },
  "es": {
    "alert": {
      "loadError": "No se pudo cargar la página solicitada. Actualice la página y vuelva a intentarlo."
    }
  },
  "fr": {
    "alert": {
      "loadError": "La page demandée ne peut être chargée. Merci de rafraîchir la page et d'essayer à nouveau."
    }
  },
  "it": {
    "alert": {
      "loadError": "Impossibile caricare la pagina richiesta. Perfavore ricarica la pagina e riprova."
    }
  },
  "ja": {
    "alert": {
      "loadError": "リクエストされたページを読み込むことができませんでした。ページを更新して、もう一度試みて下さい。"
    }
  },
  "sw": {
    "alert": {
      "loadError": "ukurasa ulioomba haukuweza kupakiwa. Tafadhali onyesha upya ukurasa na ujaribu tena"
    }
  }
}
</i18n>
